import dayjs, { type Dayjs } from "dayjs";
import { gbpPostsApi, type LocalPost, type PostTopicType } from "../../../services/gbp";
import {
  postagensRecorrentesApi,
  type FrequenciaRecorrencia,
  type PostagemRecorrente,
  type PostagemRecorrenteRequest,
} from "../../../services/postagens-recorrentes";

export const DIAS_SEMANA = [
  { valor: "MONDAY", rotulo: "Segunda", toda: "toda segunda" },
  { valor: "TUESDAY", rotulo: "Terça", toda: "toda terça" },
  { valor: "WEDNESDAY", rotulo: "Quarta", toda: "toda quarta" },
  { valor: "THURSDAY", rotulo: "Quinta", toda: "toda quinta" },
  { valor: "FRIDAY", rotulo: "Sexta", toda: "toda sexta" },
  { valor: "SATURDAY", rotulo: "Sábado", toda: "todo sábado" },
  { valor: "SUNDAY", rotulo: "Domingo", toda: "todo domingo" },
] as const;

export type RecorrenciaForm = {
  ativa: boolean;
  frequencia: FrequenciaRecorrencia;
  diaSemana: string;
  diaMes: string;
  dataFinal: string;
};

export const recorrenciaVazia = (): RecorrenciaForm => ({
  ativa: false,
  frequencia: "SEMANAL",
  diaSemana: "",
  diaMes: "",
  dataFinal: "",
});

// A recorrência do Google mora em event.recurrenceInfo: só Evento e Oferta têm event.
export const aceitaRecorrencia = (tipo?: PostTopicType) => tipo === "EVENT" || tipo === "OFFER";

const ultimoSegmento = (valor?: string) => (valor ? valor.split("/").pop() || "" : "");

export const recorrenciaDoPost = (
  post: Pick<LocalPost, "name" | "postId">,
  itens: PostagemRecorrente[],
): PostagemRecorrente | undefined => {
  const id = ultimoSegmento(post.name) || ultimoSegmento(post.postId);
  if (!id) return undefined;
  return itens.find((item) => item.externalId === post.name || ultimoSegmento(item.externalId) === id);
};

export const seloDaRecorrencia = (
  item: Pick<PostagemRecorrente, "frequencia" | "diaSemana" | "diaMes" | "dataFinal">,
): string => {
  const ate = dayjs(item.dataFinal).format("DD/MM");
  if (item.frequencia === "MENSAL") {
    return item.diaMes ? `Repete todo dia ${item.diaMes} até ${ate}` : `Repete todo mês até ${ate}`;
  }
  const dia = DIAS_SEMANA.find((d) => d.valor === item.diaSemana);
  return dia ? `Repete ${dia.toda} até ${ate}` : `Repete toda semana até ${ate}`;
};

export const erroDaRecorrencia = (
  rec: RecorrenciaForm,
  post: Pick<LocalPost, "topicType" | "event">,
  hoje: Dayjs = dayjs(),
): string | null => {
  if (!aceitaRecorrencia(post.topicType)) return "Repetição só vale para Evento ou Oferta.";
  if (!post.event?.title?.trim()) return "Informe o título.";
  const inicio = post.event.startTime ? dayjs(post.event.startTime) : null;
  const fim = post.event.endTime ? dayjs(post.event.endTime) : null;
  if (!inicio?.isValid() || !fim?.isValid()) return "Informe início e fim da primeira vez.";
  if (!fim.isAfter(inicio)) return "O fim precisa ser depois do início.";
  if (rec.frequencia === "MENSAL" && rec.diaMes) {
    const dia = Number(rec.diaMes);
    if (!Number.isInteger(dia) || dia < 1 || dia > 31) return "Dia do mês deve ser de 1 a 31.";
  }
  if (!rec.dataFinal) return "Informe a data final da repetição.";
  const dataFinal = dayjs(rec.dataFinal);
  const dia = hoje.startOf("day");
  if (!dataFinal.isAfter(dia, "day")) return "A data final precisa ser no futuro.";
  if (dataFinal.isAfter(dia.add(1, "year"), "day")) return "A repetição pode durar no máximo 1 ano.";
  if (dataFinal.isBefore(inicio, "day")) return "A data final precisa ser depois do início.";
  return null;
};

export const montarPedidoRecorrente = (post: LocalPost, rec: RecorrenciaForm): PostagemRecorrenteRequest => ({
  topicType: post.topicType,
  summary: post.summary,
  languageCode: post.languageCode,
  media: post.media?.filter((m) => m.sourceUrl).map((m) => ({ mediaFormat: m.mediaFormat, sourceUrl: m.sourceUrl })),
  event: {
    title: post.event?.title || "",
    startTime: post.event?.startTime || "",
    endTime: post.event?.endTime || "",
  },
  offer: post.topicType === "OFFER" ? post.offer : undefined,
  callToAction: post.callToAction,
  recorrencia: {
    frequencia: rec.frequencia,
    diaSemana: rec.frequencia === "SEMANAL" && rec.diaSemana ? rec.diaSemana : null,
    diaMes: rec.frequencia === "MENSAL" && rec.diaMes ? Number(rec.diaMes) : null,
    dataFinal: rec.dataFinal,
  },
});

export type ResultadoPublicacao =
  | { via: "proxy"; criado: LocalPost }
  | { via: "recorrente"; recorrencia: PostagemRecorrente };

// Sem recorrência ligada, o caminho é exatamente o de antes: proxy /api/v1/gbp com o mesmo payload.
export const publicarPostagem = async (
  publicId: string,
  payload: LocalPost,
  rec: RecorrenciaForm | null,
): Promise<ResultadoPublicacao> => {
  if (rec?.ativa && aceitaRecorrencia(payload.topicType)) {
    const recorrencia = await postagensRecorrentesApi.criar(publicId, montarPedidoRecorrente(payload, rec));
    return { via: "recorrente", recorrencia };
  }
  const criado = await gbpPostsApi.createPost(publicId, payload);
  return { via: "proxy", criado };
};
